import { ConfiguratorType } from '../types';
import { getState, subscribe } from '../state';

export type SwitchCallback = (type: ConfiguratorType) => void;

// ─── SVG thumbnail icons per configurator ────────────────────────────────────

const ICON_FURNITURE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
  <rect x="3" y="13" width="18" height="4" rx="1"/>
  <path d="M5 13V8a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v5"/>
  <path d="M3 17v2"/><path d="M21 17v2"/>
</svg>`;

const ICON_ACCESSORIES = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="3"/>
  <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
</svg>`;

const ICON_AUTOMOBILE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
  <path d="M5 17H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h14l4 4v4a2 2 0 0 1-2 2h-2"/>
  <circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/>
</svg>`;

// ─── Card data ────────────────────────────────────────────────────────────────

interface NavCard {
  type: ConfiguratorType;
  label: string;
  sub: string;
  icon: string;
}

const NAV_CARDS: NavCard[] = [
  { type: 'furniture',    label: 'Furniture',    sub: 'Sofas & seating',     icon: ICON_FURNITURE    },
  { type: 'accessories',  label: 'Accessories',  sub: 'Decor & accents',     icon: ICON_ACCESSORIES  },
  { type: 'automobile',   label: 'Automobile',   sub: 'Vehicles & parts',    icon: ICON_AUTOMOBILE   },
];

// ─── Builder ─────────────────────────────────────────────────────────────────

/**
 * Builds the left navigation panel with product cards and appends it to `container`.
 *
 * - Calls `onSwitch` when the user selects a different card.
 * - Reacts to state changes to keep the active card in sync.
 * - Returns the root panel element.
 */
export function createLeftPanel(
  container: HTMLElement,
  onSwitch: SwitchCallback
): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'cfg-left-panel';

  // ── Header ──
  const header = document.createElement('div');
  header.className = 'cfg-left-header';

  const eyebrow = document.createElement('p');
  eyebrow.className = 'cfg-left-eyebrow';
  eyebrow.textContent = '3D Configurator';

  const title = document.createElement('h1');
  title.className = 'cfg-left-title';
  title.textContent = 'Customize Your Product';

  header.appendChild(eyebrow);
  header.appendChild(title);
  panel.appendChild(header);

  // ── Card list ──
  const cardList = document.createElement('div');
  cardList.className = 'cfg-card-list';

  const cardMap = new Map<ConfiguratorType, HTMLButtonElement>();

  NAV_CARDS.forEach(({ type, label, sub, icon }) => {
    const card = document.createElement('button');
    card.className = 'cfg-card';
    card.setAttribute('aria-pressed', 'false');

    // Thumbnail
    const thumb = document.createElement('div');
    thumb.className = 'cfg-card__thumb';
    thumb.innerHTML = icon;

    // Body
    const body = document.createElement('div');
    body.className = 'cfg-card__body';

    const name = document.createElement('div');
    name.className = 'cfg-card__name';
    name.textContent = label;

    const subtitle = document.createElement('div');
    subtitle.className = 'cfg-card__sub';
    subtitle.textContent = sub;

    const dots = document.createElement('div');
    dots.className = 'cfg-card__dots';
    [0, 1, 2].forEach(() => {
      const dot = document.createElement('div');
      dot.className = 'cfg-card__dot';
      dots.appendChild(dot);
    });

    body.appendChild(name);
    body.appendChild(subtitle);
    body.appendChild(dots);

    card.appendChild(thumb);
    card.appendChild(body);
    cardList.appendChild(card);
    cardMap.set(type, card);

    card.addEventListener('click', () => {
      const state = getState();
      if (state.isLoading || state.activeConfigurator === type) return;
      onSwitch(type);
    });
  });

  panel.appendChild(cardList);

  // ── Footer ──
  const footer = document.createElement('div');
  footer.className = 'cfg-left-footer';

  const desc = document.createElement('p');
  desc.className = 'cfg-left-footer__desc';
  desc.textContent = 'Explore our full collection and find the perfect configuration for your space.';

  const exploreBtn = document.createElement('button');
  exploreBtn.className = 'cfg-explore-btn';
  exploreBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>Explore More`;

  const contact = document.createElement('p');
  contact.className = 'cfg-left-footer__contact';
  contact.innerHTML = `Need help? <a href="#">Contact us</a>`;

  footer.appendChild(desc);
  footer.appendChild(exploreBtn);
  footer.appendChild(contact);
  panel.appendChild(footer);

  // ── State sync ──
  const syncCards = (active: ConfiguratorType, loading: boolean): void => {
    cardMap.forEach((card, t) => {
      const isActive = t === active;
      card.classList.toggle('cfg-card--active', isActive);
      card.setAttribute('aria-pressed', String(isActive));
      card.disabled = loading;
    });
  };

  const { activeConfigurator, isLoading } = getState();
  syncCards(activeConfigurator, isLoading);

  subscribe((state) => syncCards(state.activeConfigurator, state.isLoading));

  container.appendChild(panel);
  return panel;
}
